import React from 'react';
import {
  Box,
  Drawer,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { McpEndpointRow, UptimePayload } from './types';
import { formatNaN } from './helpers';

type Props = {
  open: boolean;
  endpoint: McpEndpointRow | null;
  connectionId: string | null;
  uptime: (UptimePayload & { ok?: boolean }) | undefined;
  uptimeLoading: boolean;
  appStats: any;
  statsLoading: boolean;
  toolStats: Array<{ connection_id: string; call_count: number; error_rate_pct: number | null; avg_latency_ms: number | null }>;
  onClose: () => void;
};

export default function McpHealthDrawer({
  open,
  endpoint,
  connectionId,
  uptime,
  uptimeLoading,
  appStats,
  statsLoading,
  toolStats,
  onClose,
}: Props) {
  const segments = uptime?.segments || [];

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', sm: 480 } } }}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">{endpoint?.name || 'MCP health'}</Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {endpoint && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace', mb: 2 }}>
            {endpoint.app_id}
            {connectionId && ` · ${connectionId}`}
          </Typography>
        )}

        <Typography variant="subtitle2" gutterBottom>Uptime (7d)</Typography>
        {uptimeLoading ? (
          <Skeleton variant="rounded" height={80} sx={{ mb: 2 }} />
        ) : !uptime?.has_data ? (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            No telemetry in window — uptime cannot be calculated.
          </Typography>
        ) : (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="body2"><strong>{uptime.uptime_pct}%</strong> uptime</Typography>
              <Typography variant="body2" color="success.main">{uptime.total_up_minutes}m up</Typography>
              <Typography variant="body2" color="error.main">{uptime.total_down_minutes}m down</Typography>
            </Box>
            <Box sx={{ display: 'flex', height: 24, borderRadius: 0.5, overflow: 'hidden', border: 1, borderColor: 'divider' }}>
              {segments.map((seg, i) => (
                <Box
                  key={`${seg.start}-${i}`}
                  title={`${seg.status} ${new Date(seg.start).toLocaleString()} – ${new Date(seg.end).toLocaleString()}`}
                  sx={{ flex: 1, minWidth: 1, bgcolor: seg.status === 'up' ? 'success.main' : 'error.main' }}
                />
              ))}
            </Box>
          </Box>
        )}

        <Typography variant="subtitle2" gutterBottom>Tool breakdown (24h telemetry)</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Tool</TableCell>
              <TableCell align="right">Calls</TableCell>
              <TableCell align="right">Error rate</TableCell>
              <TableCell align="right">Avg latency</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {toolStats.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">No tool stats in window</Typography>
                </TableCell>
              </TableRow>
            ) : (
              toolStats.map((t) => (
                <TableRow key={t.connection_id}>
                  <TableCell sx={{ fontFamily: 'monospace', fontSize: 12 }}>
                    {t.connection_id.split(':').slice(1).join(':') || t.connection_id}
                  </TableCell>
                  <TableCell align="right">{t.call_count}</TableCell>
                  <TableCell align="right">{formatNaN(t.error_rate_pct, '%')}</TableCell>
                  <TableCell align="right">{formatNaN(t.avg_latency_ms, 'ms')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {statsLoading ? (
          <Skeleton variant="text" sx={{ mt: 2 }} />
        ) : appStats?.top_tools?.length ? (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Audit log top tools</Typography>
            {appStats.top_tools.map((t: { tool: string; count: number; avg_duration_ms?: number }) => (
              <Typography key={t.tool} variant="caption" display="block">
                {t.tool}: {t.count} calls
                {t.avg_duration_ms != null ? ` · ${t.avg_duration_ms}ms avg` : ''}
              </Typography>
            ))}
          </Box>
        ) : null}
      </Box>
    </Drawer>
  );
}
